import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'quiet' | 'board' | 'danger';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-leaf-600 text-paper-50 border-leaf-700 hover:bg-leaf-500 active:translate-y-px disabled:bg-paper-300 disabled:text-ink-500 disabled:border-paper-400',
  quiet:
    'bg-paper-50 text-ink-900 border-paper-300 hover:bg-paper-100 hover:border-paper-400 active:translate-y-px disabled:text-ink-500',
  board:
    'bg-slate-board text-chalk border-slate-board hover:bg-slate-boardlight active:translate-y-px',
  // Tomato is reserved for destructive actions only, so red always means one thing.
  danger:
    'bg-tomato-600 text-paper-50 border-tomato-700 hover:bg-tomato-500 active:translate-y-px disabled:bg-paper-300 disabled:text-ink-500',
};

export function Button({
  variant = 'quiet',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={`sign inline-flex items-center justify-center gap-2 rounded-[3px] border px-4 py-2 text-[13px] transition-colors disabled:cursor-not-allowed ${VARIANTS[variant]} ${className}`}
    />
  );
}

export function Field({
  label,
  hint,
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <label className="block">
      <span className="sign block text-[11px] text-ink-700">{label}</span>
      <input
        {...props}
        className={`mt-1.5 w-full rounded-[3px] border border-paper-300 bg-paper-50 px-3 py-2 text-ink-900 placeholder:text-ink-500/60 focus:border-leaf-600 ${className}`}
      />
      {hint && <span className="mt-1 block text-[13px] text-ink-500">{hint}</span>}
    </label>
  );
}

/**
 * A short uppercase flag. Tone carries the meaning, so it is never decorative:
 * `deal` is ochre and appears only where a customer saves money.
 */
export function Badge({
  tone = 'muted',
  children,
}: {
  tone?: 'deal' | 'good' | 'muted' | 'danger';
  children: ReactNode;
}) {
  const tones = {
    deal: 'bg-ochre-400 text-ink-950',
    good: 'bg-leaf-300/45 text-leaf-700',
    muted: 'bg-paper-200 text-ink-500',
    danger: 'bg-tomato-500/15 text-tomato-700',
  };
  return (
    <span className={`sign shrink-0 rounded-[2px] px-2 py-0.5 text-[9.5px] ${tones[tone]}`}>
      {children}
    </span>
  );
}

/** Page title and one line of orientation. Every screen opens the same way. */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-3xl leading-tight text-ink-950">{title}</h1>
        {subtitle && <p className="mt-1.5 max-w-xl text-[14px] text-ink-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`tag p-5 ${className}`}>{children}</div>;
}

export function Board({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`board p-5 ${className}`}>{children}</div>;
}

/** Small uppercase label above a value. Used everywhere numbers are presented. */
export function Stat({
  label,
  value,
  sub,
  tone = 'paper',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'paper' | 'board';
}) {
  const muted = tone === 'board' ? 'text-chalk/55' : 'text-ink-500';
  return (
    <div>
      <div className={`sign text-[10px] ${muted}`}>{label}</div>
      <div className="tabular mt-1 text-2xl leading-none">{value}</div>
      {sub && <div className={`mt-1.5 text-[13px] ${muted}`}>{sub}</div>}
    </div>
  );
}

export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'error' | 'good';
  children: ReactNode;
}) {
  const tones = {
    info: 'border-paper-400 bg-paper-200 text-ink-900',
    warn: 'border-ochre-500 bg-ochre-400/25 text-ink-900',
    error: 'border-tomato-600 bg-tomato-500/12 text-ink-900',
    good: 'border-leaf-600 bg-leaf-300/25 text-ink-900',
  };
  return (
    <div className={`rounded-[3px] border-l-[3px] px-4 py-3 text-[14px] ${tones[tone]}`}>
      {children}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <p className="sign py-10 text-center text-[11px] text-ink-500">{label}…</p>;
}

export function Empty({ title, body }: { title: string; body?: string }) {
  return (
    <div className="tag border-dashed px-6 py-12 text-center">
      <p className="sign text-[12px] text-ink-700">{title}</p>
      {body && <p className="mt-2 text-[14px] text-ink-500">{body}</p>}
    </div>
  );
}
